import React from 'react';
import { Box, Button, Container, Typography, Paper, Stack } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const RANGER_QUOTES = [
  {
    title: "10-404: PAGE NOT FOUND",
    message: "This road don't lead nowhere, partner. Looks like you've driven off the map.",
    subtext: "Even GPS can't find this one.",
  },
  {
    title: "ROAD CLOSED AHEAD",
    message: "Whoa there, cowboy! This page has been cleared from the highway.",
    subtext: "No debris here... just empty pavement.",
  },
  {
    title: "STRANDED PAGE",
    message: "This page ran out of gas somewhere on US-75 and we can't locate it.",
    subtext: "Unlike you, we can't assist this one.",
  },
  {
    title: "WRONG EXIT",
    message: "You took the wrong exit, partner. This ain't the page you're looking for.",
    subtext: "Make a U-turn when legally permitted.",
  },
  {
    title: "DETOUR REQUIRED",
    message: "Construction zone ahead! This page is under... well, it just doesn't exist.",
    subtext: "Our volunteers searched, but came up empty.",
  },
];

export default function NotFound() {
  const navigate = useNavigate();
  const quote = RANGER_QUOTES[Math.floor(Math.random() * RANGER_QUOTES.length)];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Hero Background with Image */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#003366',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/dallas1.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,51,102,0.92) 0%, rgba(0,38,77,0.88) 50%, rgba(0,51,102,0.85) 100%)',
            },
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, py: 8 }}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: 'rgba(255,255,255,0.98)',
              borderRadius: 4,
              border: '4px solid',
              borderColor: '#ffc107',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
          >
            {/* Warning Icon */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: '#fff3e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                border: '3px solid #ffc107',
              }}
            >
              <WarningAmberIcon
                sx={{
                  fontSize: 60,
                  color: '#f57c00',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                    '100%': { opacity: 1, transform: 'scale(1)' },
                  },
                }}
              />
            </Box>

            {/* 404 Number */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '4rem', sm: '6rem', md: '7rem' },
                fontWeight: 900,
                color: '#003366',
                textShadow: '3px 3px 6px rgba(0,51,102,0.2)',
                mb: 1,
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: '-0.02em',
              }}
            >
              404
            </Typography>

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#f57c00',
                textTransform: 'uppercase',
                letterSpacing: 3,
                mb: 3,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {quote.title}
            </Typography>

            {/* Message */}
            <Typography
              variant="h6"
              sx={{
                color: '#333',
                mb: 2,
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                lineHeight: 1.6,
              }}
            >
              "{quote.message}"
            </Typography>

            {/* Subtext */}
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                mb: 4,
              }}
            >
              {quote.subtext}
            </Typography>

            {/* Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                component={Link}
                to="/"
                variant="contained"
                size="large"
                startIcon={<HomeIcon />}
                sx={{
                  bgcolor: '#ffc107',
                  color: '#003366',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: '#ffca28',
                  },
                }}
              >
                Return Home
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: '#003366',
                  color: '#003366',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#003366',
                    bgcolor: 'rgba(0,51,102,0.08)',
                  },
                }}
              >
                Go Back
              </Button>
            </Stack>

            {/* Footer Note */}
            <Box sx={{ mt: 5, pt: 4, borderTop: '1px dashed #ccc' }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#888',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                🤠 Texas Highway Rangers — Texans Helping Texans, One Mile at a Time
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#aaa',
                  fontFamily: 'monospace',
                  mt: 1,
                  display: 'block',
                }}
              >
                Error Code: PAGE_WENT_10-7
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Bottom accent bar */}
      <Box
        sx={{
          height: 6,
          background: 'linear-gradient(90deg, #ffc107 0%, #ff9800 50%, #f57c00 100%)',
        }}
      />
    </Box>
  );
}
